import React, { useRef, useEffect, useState } from 'react';
import { Tooltip, Typography } from '@mui/material';
import type { TypographyProps } from '@mui/material';
import { styled } from '@mui/material/styles';

const TruncatedText = styled(Typography)(() => ({
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: 1.2,
  maxHeight: '2.4em', // 2 lines at 1.2 line-height
  wordBreak: 'break-word',
  cursor: 'pointer',
  width: '100%',
  minWidth: 0, // Allow flex shrinking
}));

interface TwoLineTextWithTooltipProps
  extends Omit<TypographyProps, 'children'> {
  text: string;
  /** Whether to show tooltip even when text is not truncated */
  alwaysShowTooltip?: boolean;
}

export const TwoLineTextWithTooltip: React.FC<TwoLineTextWithTooltipProps> = ({
  text,
  alwaysShowTooltip = false,
  ...typographyProps
}) => {
  const textRef = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = React.useCallback(() => {
    const element = textRef.current;
    if (element) {
      // Check if content is truncated by comparing scroll height to client height
      const isOverflowing = element.scrollHeight > element.clientHeight;
      setIsTruncated(isOverflowing);
    }
  }, []);

  useEffect(() => {
    checkTruncation();
  }, [text, checkTruncation]);

  // Watch for container resize (e.g., when table columns are resized)
  useEffect(() => {
    const element = textRef.current;
    if (!element) {
      return;
    }

    // Check if ResizeObserver is available
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      checkTruncation();
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [checkTruncation]);

  if (!text) {
    return <Typography {...typographyProps}>-</Typography>;
  }

  const shouldShowTooltip = alwaysShowTooltip || isTruncated;

  return (
    <Tooltip
      title={text}
      disableHoverListener={!shouldShowTooltip}
      placement="top"
      arrow
    >
      <TruncatedText ref={textRef} {...typographyProps}>
        {text}
      </TruncatedText>
    </Tooltip>
  );
};

export default TwoLineTextWithTooltip;
